import { doc, updateDoc, getDoc, serverTimestamp, addDoc, collection, query, where, getDocs, orderBy, increment } from 'firebase/firestore';
import { db } from '../config/firebase';

export const NotificationType = {
  // Soru-Cevap bildirimleri
  QUESTION_ANSWERED: 'QUESTION_ANSWERED',
  QUESTION_RESOLVED: 'QUESTION_RESOLVED',
  
  // Mesajlaşma bildirimleri
  USER_NEW_MESSAGE: 'USER_NEW_MESSAGE',      // Kullanıcıya gelen mesaj
  ADMIN_NEW_MESSAGE: 'ADMIN_NEW_MESSAGE',    // Admine/editöre gelen mesaj
  
  // İstek bildirimleri
  REQUEST_CREATED: 'REQUEST_CREATED',
  REQUEST_RESPONDED: 'REQUEST_RESPONDED',
  REQUEST_COMPLETED: 'REQUEST_COMPLETED',
  REQUEST_STATUS_CHANGED: 'REQUEST_STATUS_CHANGED',
  REQUEST_MESSAGE_RECEIVED: 'REQUEST_MESSAGE_RECEIVED',
  REQUEST_COMPLETION_WARNING: 'REQUEST_COMPLETION_WARNING',
  
  // Sistem bildirimleri
  SYSTEM_ANNOUNCEMENT: 'SYSTEM_ANNOUNCEMENT'
};

export const createNotification = async (userId, type, data) => {
  try {
    if (!userId || !type || !data) {
      console.error('Bildirim oluşturma hatası: Eksik parametreler', { userId, type, data });
      throw new Error('Bildirim için gerekli bilgiler eksik');
    }

    const notificationData = {
      userId,
      type,
      data,
      read: false,
      createdAt: serverTimestamp()
    };

    let message = '';
    switch (type) {
      case NotificationType.REQUEST_CREATED:
        message = `Yeni bir özel istek oluşturuldu: ${data.requestTitle}`;
        break;
      case NotificationType.REQUEST_RESPONDED:
        message = `Özel isteğinize yanıt verildi: ${data.requestTitle}`;
        break;
      case NotificationType.REQUEST_COMPLETED:
        message = `Özel isteğiniz tamamlandı: ${data.requestTitle}`;
        break;
      case NotificationType.QUESTION_RESOLVED:
        message = `Sorunuz çözüldü olarak işaretlendi: ${data.questionTitle}`;
        break;
      case NotificationType.QUESTION_ANSWERED:
        message = `Sorunuza yeni bir yanıt geldi: ${data.questionTitle}`;
        break;
      default:
        message = 'Yeni bir bildiriminiz var';
    }

    notificationData.message = message;

    console.log('Bildirim oluşturuluyor:', notificationData);

    const notificationsRef = collection(db, 'notifications');
    const docRef = await addDoc(notificationsRef, notificationData);

    console.log('Bildirim başarıyla oluşturuldu:', docRef.id);

    // Kullanıcının unread_notifications sayısını güncelle
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      unreadNotifications: increment(1)
    });

    return docRef.id;
  } catch (error) {
    console.error('Bildirim oluşturulurken hata:', error);
    throw error;
  }
};

// Soru yanıtlandığında bildirim gönderme
export const createQuestionAnswerNotification = async (questionId, questionTitle, userId, answeredBy) => {
  try {
    await createNotification(userId, NotificationType.QUESTION_ANSWERED, {
      questionId,
      questionTitle,
      answeredBy: answeredBy.displayName || answeredBy.email,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Soru yanıt bildirimi hatası:', error);
    throw error;
  }
};

export const createQuestionResolvedNotification = async (questionId, questionTitle, userId, resolvedBy) => {
  try {
    console.log('Soru çözüldü bildirimi oluşturuluyor:', {
      questionId,
      questionTitle,
      userId,
      resolvedBy
    });

    const notificationId = await createNotification(userId, NotificationType.QUESTION_RESOLVED, {
      questionId,
      questionTitle,
      resolvedBy,
      timestamp: serverTimestamp()
    });

    console.log('Soru çözüldü bildirimi oluşturuldu:', notificationId);
    return notificationId;
  } catch (error) {
    console.error('Soru çözüldü bildirimi oluşturma hatası:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId, userId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true
    });

    // Kullanıcının unread_notifications sayısını azalt
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      unreadNotifications: increment(-1)
    });
  } catch (error) {
    console.error('Bildirim güncelleme hatası:', error);
  }
};

export const getNotifications = async (userId) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Bildirimler alınırken hata:', error);
    throw error;
  }
};

export const getNotificationMessage = (notification) => {
  switch (notification.type) {
    case NotificationType.QUESTION_ANSWERED:
      return {
        title: 'Sorunuza Yeni Yanıt',
        message: `"${notification.data.questionTitle}" sorunuza yeni bir yanıt geldi.`,
        link: `/qa/${notification.data.questionId}#answer-${notification.data.answerId}`
      };
    
    case NotificationType.QUESTION_RESOLVED:
      return {
        title: 'Soru Çözüldü',
        message: `"${notification.data.questionTitle}" sorunuz çözüldü olarak işaretlendi.`,
        link: `/qa/${notification.data.questionId}`
      };

    case NotificationType.REQUEST_CREATED:
      return {
        title: 'Yeni Özel İstek',
        message: `"${notification.data.requestTitle}" başlıklı yeni bir özel istek oluşturuldu.`,
        link: `/admin/requests/${notification.data.requestId}`
      };

    case NotificationType.REQUEST_RESPONDED:
      return {
        title: 'İsteğinize Yanıt Verildi',
        message: `"${notification.data.requestTitle}" başlıklı isteğinize ${notification.data.respondedBy} tarafından yanıt verildi.`,
        link: `/requests/${notification.data.requestId}#message-${notification.data.messageId}`
      };

    case NotificationType.REQUEST_COMPLETED:
      return {
        title: 'İsteğiniz Tamamlandı',
        message: `"${notification.data.requestTitle}" başlıklı isteğiniz ${notification.data.completedBy} tarafından tamamlandı.`,
        link: `/requests/${notification.data.requestId}`
      };

    case NotificationType.USER_NEW_MESSAGE:
      return {
        title: 'Yeni Mesaj',
        message: `${notification.data.senderName} size yeni bir mesaj gönderdi: "${notification.data.messagePreview}"`,
        link: `/requests/${notification.data.requestId}#message-${notification.data.messageId}`
      };

    case NotificationType.ADMIN_NEW_MESSAGE:
      return {
        title: 'Yeni Yönetici Mesajı',
        message: `${notification.data.senderName} tarafından yeni bir mesaj: "${notification.data.messagePreview}"`,
        link: `/admin/requests/${notification.data.requestId}#message-${notification.data.messageId}`
      };

    case NotificationType.REQUEST_STATUS_CHANGED:
      return {
        title: 'İstek Durumu Güncellendi',
        message: `"${notification.data.requestTitle}" başlıklı isteğinizin durumu "${notification.data.newStatus}" olarak güncellendi.`,
        link: `/requests/${notification.data.requestId}`
      };

    case NotificationType.REQUEST_MESSAGE_RECEIVED:
      return {
        title: 'Yeni İstek Mesajı',
        message: `${notification.data.senderName} tarafından yeni bir mesaj: "${notification.data.messagePreview}"`,
        link: `${notification.data.role === 'admin' ? '/admin' : ''}/requests/${notification.data.requestId}#message-${notification.data.messageId}`
      };

    case NotificationType.REQUEST_COMPLETION_WARNING:
      return {
        title: 'İstek Tamamlama Uyarısı',
        message: `"${notification.data.requestTitle}" başlıklı isteğinizin tamamlanma tarihi yaklaşıyor.`,
        link: `/requests/${notification.data.requestId}`
      };

    case NotificationType.SYSTEM_ANNOUNCEMENT:
      return {
        title: 'Sistem Duyurusu',
        message: notification.data.message,
        link: notification.data.link || '/'
      };

    default:
      return {
        title: 'Yeni Bildirim',
        message: 'Yeni bir bildiriminiz var.',
        link: '/'
      };
  }
};

export const getUnreadNotificationCount = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const notifications = userDoc.data().notifications || [];
      return notifications.filter(n => !n.read).length;
    }
    return 0;
  } catch (error) {
    console.error('Okunmamış bildirim sayısı alınırken hata:', error);
    return 0;
  }
};

// Admin ve editörlere bildirim gönderme yardımcı fonksiyonu
const sendNotificationToAdminAndEditors = async (type, data) => {
  try {
    // Admin rolüne sahip kullanıcılara bildirim gönder
    const usersRef = collection(db, 'users');
    const adminQuery = query(usersRef, where('role', '==', 'admin'));
    const adminSnapshot = await getDocs(adminQuery);
    
    for (const adminDoc of adminSnapshot.docs) {
      if (adminDoc.id !== data.userId) { // Kendisine bildirim gönderme
        await createNotification(adminDoc.id, type, data);
      }
    }

    // Editor rolüne sahip kullanıcılara bildirim gönder
    const editorQuery = query(usersRef, where('role', '==', 'editor'));
    const editorSnapshot = await getDocs(editorQuery);
    
    for (const editorDoc of editorSnapshot.docs) {
      if (editorDoc.id !== data.userId) { // Kendisine bildirim gönderme
        await createNotification(editorDoc.id, type, data);
      }
    }
  } catch (error) {
    console.error('Admin ve editörlere bildirim gönderilirken hata:', error);
    throw error;
  }
};

// Özel istek oluşturulduğunda bildirim gönderme
export const createSpecialRequestNotification = async (requestData) => {
  try {
    const notificationData = {
      requestId: requestData.id,
      requestTitle: requestData.title,
      senderName: requestData.userName || requestData.userEmail || 'Anonim',
      priority: requestData.priority || 'normal',
      userId: requestData.userId,
      category: requestData.category,
      timestamp: serverTimestamp()
    };

    // Admin ve editörlere bildirim gönder
    await sendNotificationToAdminAndEditors(NotificationType.REQUEST_CREATED, notificationData);

    console.log('Özel istek bildirimleri başarıyla gönderildi');
  } catch (error) {
    console.error('Özel istek bildirimi gönderme hatası:', error);
    throw error;
  }
};

// Yeni mesaj bildirimi gönderme
export const createMessageNotification = async (messageData) => {
  try {
    const { requestId, senderId, content, requestTitle } = messageData;
    
    // Mesajı gönderen kişinin bilgilerini al
    const senderDoc = await getDoc(doc(db, 'users', senderId));
    const senderData = senderDoc.data();
    const senderName = senderData.displayName || senderData.email;

    // İsteğin detaylarını al
    const requestDoc = await getDoc(doc(db, 'requests', requestId));
    const requestData = requestDoc.data();

    // Mesaj önizlemesi (ilk 50 karakter)
    const messagePreview = content.length > 50 ? `${content.substring(0, 50)}...` : content;

    if (senderData.role === 'admin' || senderData.role === 'editor') {
      // Admin/editör mesaj gönderdiyse, kullanıcıya bildirim gönder
      await createNotification(requestData.userId, NotificationType.REQUEST_MESSAGE_RECEIVED, {
        requestId,
        requestTitle,
        messagePreview,
        senderName,
        senderId,
        timestamp: serverTimestamp()
      });
    } else {
      // Kullanıcı mesaj gönderdiyse, admin ve editörlere bildirim gönder
      const usersRef = collection(db, 'users');
      const adminQuery = query(usersRef, where('role', 'in', ['admin', 'editor']));
      const adminSnapshot = await getDocs(adminQuery);
      
      for (const adminDoc of adminSnapshot.docs) {
        await createNotification(adminDoc.id, NotificationType.REQUEST_MESSAGE_RECEIVED, {
          requestId,
          requestTitle,
          messagePreview,
          senderName,
          senderId,
          timestamp: serverTimestamp()
        });
      }
    }
  } catch (error) {
    console.error('Mesaj bildirimi gönderme hatası:', error);
    throw error;
  }
};

export const createRequestStatusNotification = async (userId, requestData, newStatus) => {
  try {
    return await createNotification(userId, NotificationType.REQUEST_STATUS_CHANGED, {
      requestId: requestData.id,
      requestTitle: requestData.title,
      oldStatus: requestData.status,
      newStatus: newStatus,
      updatedBy: requestData.lastUpdatedBy,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('İstek durumu bildirimi oluşturma hatası:', error);
    throw error;
  }
};

export const createSystemNotification = async (userIds, message, type = NotificationType.SYSTEM_ANNOUNCEMENT) => {
  try {
    const notifications = userIds.map(userId => 
      createNotification(userId, type, {
        message,
        timestamp: serverTimestamp()
      })
    );
    
    await Promise.all(notifications);
  } catch (error) {
    console.error('Sistem bildirimi oluşturma hatası:', error);
    throw error;
  }
}; 